<script setup lang="ts">
import type { FinancingStatus, FinancingSteps, UseFinancingsParams } from '~/store-v2/financings/useFinancings'
import { SolTabs } from '@solfacil/girassol'
import IconPlus from '~icons/material-symbols/add'
import ListActions from '~/components/list/ListActions.vue'
import ListProjects from '~/components/list/ListProjects.vue'
import { useFinancingFilters } from '~/composables/useFinancingFilters'
import { FEATURE_FLAGS } from '~/constants'
import useAuth from '~/store-v2/auth/useAuth'
import { useFeatureFlag } from '~/store-v2/feature-flag/useFeatureFlag'
import useFinancings from '~/store-v2/financings/useFinancings'
import { INCENTIVE_CAMPAIGN_TYPE_REVALIDATION, INCENTIVE_TARGET_TYPE_PARTNER } from '~/store-v2/incentives/incentivesQuery.constants'
import useTargetIncentivesSummary from '~/store-v2/incentives/useTargetIncentivesSummary'
import usePartner from '~/store-v2/partners/usePartner'
import useRevalidationSummary from '~/store-v2/revalidations/useRevalidationSummary'
import ReassessmentModal from '~/ui-blocks/Formalization/blocks/reassessmentModal.vue'
import RevalidationBannerGroup from '~/ui-blocks/Revalidations/RevalidationBannerGroup.vue'
import RevalidationsTab from '~/ui-blocks/Revalidations/RevalidationsTab.vue'
import RevalidationWelcomeModal from '~/ui-blocks/Revalidations/RevalidationWelcomeModal.vue'
import { COMBO_TYPE_FILTER, comboTypeFromParams, comboTypeToParams, isComboTypeFilter } from '~/utils/financing-flow/comboType'

const REVALIDATION_WELCOME_MODAL_VIEWED_KEY = 'revalidation_welcome_modal_viewed'

const revalidationBannerAlt = 'Pessoa revalidando instalações pelo celular'
const revalidationBannerCtaLabel = 'Ver revalidações pendentes'

const isEnabledToBNPL = ref(false)
const impersonateStore = useImpersonateStore()
const { t } = useI18n()
const router = useRouter()
const route = useRoute<any>()

const { data: bnplFeatureFlag } = useFeatureFlag<boolean>(FEATURE_FLAGS.BNPL, false)

const { data: revalidationTabFlag } = useFeatureFlag<boolean>(FEATURE_FLAGS.REVALIDATION_TAB, false)

const revalidationSummaryEnabled = computed(() => revalidationTabFlag.value === true)
const { data: revalidationSummary } = useRevalidationSummary(revalidationSummaryEnabled)

const openModal = ref(false)
const revalidationWelcomeModalOpen = ref(false)
const tabListModel = ref([
  { key: 'all', label: 'Todos', active: true, disabled: false },
  { key: 'reassessment', label: 'Repescagem', active: false, disabled: false },
])

const initialProvider = route.query.provider as string || ''

function activateTabByKey(key: string) {
  tabListModel.value.forEach((tab) => {
    tab.active = tab.key === key
  })
}

function handleRevalidationWelcomeClose() {
  revalidationWelcomeModalOpen.value = false
}

function handleRevalidationWelcomeNeverShowAgain() {
  localStorage.setItem(REVALIDATION_WELCOME_MODAL_VIEWED_KEY, 'true')
}

function handleGoToRevalidationFromWelcome() {
  revalidationWelcomeModalOpen.value = false
  nextTick(() => {
    document.getElementById('revalidations-session-anchor')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  })
}

watch(revalidationTabFlag, (enabled) => {
  const hasRevalidationTab = tabListModel.value.some(tab => tab.key === 'revalidation')
  if (enabled && !hasRevalidationTab) {
    tabListModel.value.push({ key: 'revalidation', label: 'Revalidação', active: initialProvider === 'revalidation', disabled: false })
    if (initialProvider === 'revalidation') {
      tabListModel.value.forEach((tab) => {
        if (tab.key !== 'revalidation') tab.active = false
      })
    }
    maybeOpenRevalidationWelcomeModal()
  }
  else if (!enabled && hasRevalidationTab) {
    tabListModel.value = tabListModel.value.filter(tab => tab.key !== 'revalidation')
  }
}, { immediate: true })

watch(() => route.query.provider, (provider) => {
  if (provider === 'revalidation' && tabListModel.value.some(tab => tab.key === 'revalidation'))
    activateTabByKey('revalidation')
})

watch(() => tabListModel.value.find(tab => tab.key === 'revalidation')?.active, (active) => {
  if (active)
    maybeOpenRevalidationWelcomeModal()
})

const hasActiveQuickFilter = ref(false)

const sortByOptions = [
  { name: t('financing.sort_by.updated_at_desc'), value: 'updated_at:desc' },
  { name: t('financing.sort_by.created_at_desc'), value: 'created_at:desc' },
  { name: t('financing.sort_by.created_at_asc'), value: 'created_at:asc' },
  { name: t('financing.sort_by.expires_at_asc'), value: 'expires_at:asc' },
]
const initialOrderBy = route.query.order_by?.toString() || 'updated_at:desc'
const sortBy = ref(sortByOptions.find(option => option.value === initialOrderBy) || sortByOptions[0])

if (initialProvider === 'reassessment') {
  tabListModel.value[0].active = false
  tabListModel.value[1].active = true
}

function parseBooleanParam(value: any): boolean | undefined {
  if (value === 'true') return true
  if (value === 'false') return false
  return undefined
}

function parseNumberParam(value: any): number | undefined {
  return value ? Number(value) : undefined
}

function parseQueryParams(query: any) {
  const steps = query.steps
    ? (Array.isArray(query.steps)
        ? query.steps
        : typeof query.steps === 'string'
          ? query.steps.split(',').filter(Boolean)
          : [query.steps]).filter(Boolean) as FinancingSteps[]
    : []

  const status = query.status
    ? (Array.isArray(query.status)
        ? query.status
        : typeof query.status === 'string'
          ? query.status.split(',').filter(Boolean)
          : [query.status]).filter(Boolean) as FinancingStatus[]
    : []

  return {
    page: Number(query.page) || 1,
    steps,
    status,
    query: query.query?.toString() || '',
    order_by: query.order_by?.toString() || 'updated_at:desc',
    provider: query.provider?.toString() || '',
    is_installed: parseBooleanParam(query.is_installed),
    formalized_lte: parseNumberParam(query.formalized_lte),
    formalized_gte: parseNumberParam(query.formalized_gte),
    is_expired: parseBooleanParam(query.is_expired),
    is_canceled: parseBooleanParam(query.is_canceled),
    ...comboTypeToParams(isComboTypeFilter(query.combo_type) ? query.combo_type : COMBO_TYPE_FILTER.ALL),
  }
}

function buildParamsFromQuery(query: any, baseParams: UseFinancingsParams): UseFinancingsParams {
  const parsed = parseQueryParams(query)
  return {
    ...baseParams,
    ...parsed,
  }
}

const defaultQueryParams = parseQueryParams(route.query)

const {
  data: auth,
} = useAuth()

const { checkIsImpersonate } = useImpersonate()
watch(() => auth.value?.me?.perfil, (perfil) => {
  if (perfil === 'admin') {
    checkIsImpersonate()
  }
}, { immediate: true })

const basePartnerId = computed(() => {
  const perfil = auth.value?.me?.perfil

  if (perfil === 'admin' && impersonateStore.partnerId > 0) {
    return impersonateStore.partnerId.toString()
  }
  return auth.value?.person?.partner?.id?.toString() || ''
})

const { data: incentiveData, isError: isIncentiveError } = useTargetIncentivesSummary({
  targetId: basePartnerId,
  targetType: INCENTIVE_TARGET_TYPE_PARTNER,
  campaignType: INCENTIVE_CAMPAIGN_TYPE_REVALIDATION,
})

const hasIncentiveBanner = computed(() => !isIncentiveError.value && !!incentiveData.value)

function maybeOpenRevalidationWelcomeModal() {
  const isRevalidationTabActive = tabListModel.value.some(tab => tab.key === 'revalidation' && tab.active)
  if (isRevalidationTabActive && hasIncentiveBanner.value && !localStorage.getItem(REVALIDATION_WELCOME_MODAL_VIEWED_KEY))
    revalidationWelcomeModalOpen.value = true
}

watch(hasIncentiveBanner, (has) => {
  if (has)
    maybeOpenRevalidationWelcomeModal()
})

const financingsPartnerId = computed(() => {
  const perfil = auth.value?.me?.perfil
  if (perfil !== 'admin') {
    return basePartnerId.value
  }
  if (impersonateStore.partnerId > 0) {
    return basePartnerId.value
  }
  return ''
})

const {
  params: paramsFilters,
  applyFilters,
  applyComboType,
  clearFilters: clearFiltersBase,
  handleQuickFilterChange,
  setParams,
} = useFinancingFilters({
  initialParams: {
    ...defaultQueryParams,
    page_size: 10,
    partner_id: '',
    provider: initialProvider || defaultQueryParams.provider,
  },
})

watch(financingsPartnerId, (newPartnerId) => {
  setParams({ ...paramsFilters.value, partner_id: newPartnerId })
}, { immediate: true })

const {
  data: financings,
  isLoading: isLoadingFinancings,
  error: errorFinancings,
  prefetchPage,
} = useFinancings(paramsFilters)

const {
  data: partner,
  isLoading: isLoadingPartner,
} = usePartner(basePartnerId)

const hasReassessmentItems = ref(false)
const hasPendingReassessmentModal = ref(false)

const introViewed = ref(false)
let introCheckInterval: ReturnType<typeof setInterval> | null = null

function syncIntroViewed() {
  introViewed.value = Boolean(
    localStorage.getItem('intro_modal:home:viewed')
    || localStorage.getItem('intro_modal:internal:viewed'),
  )
}

const {
  params: reassessmentParams,
  applyFilters: applyReassessmentFilters,
  applyComboType: applyReassessmentComboType,
  clearFilters: clearReassessmentFilters,
  handleQuickFilterChange: handleReassessmentQuickFilterChange,
  setParams: setReassessmentParams,
} = useFinancingFilters({
  initialParams: {
    ...(initialProvider === 'reassessment'
      ? defaultQueryParams
      : {
          page: 1,
          steps: [],
          status: [],
          query: '',
          order_by: 'updated_at:desc',
          provider: '',
          is_installed: undefined,
          formalized_lte: undefined,
          formalized_gte: undefined,
          is_expired: undefined,
          is_canceled: undefined,
          ...comboTypeToParams(COMBO_TYPE_FILTER.ALL),
        }),
    page_size: 10,
    partner_id: '',
    provider: 'reassessment',
  },
})

const userIsOnBlockedList = computed(() => {
  return partner.value?.blocklist?.is_blocked || false
})

watch(financingsPartnerId, (newPartnerId) => {
  setReassessmentParams({ ...reassessmentParams.value, partner_id: newPartnerId })
}, { immediate: true })

const { data: reassessmentFinancings, isLoading: isLoadingReassessment, prefetchPage: prefetchReassessmentPage } = useFinancings(reassessmentParams)

watch([reassessmentFinancings, isLoadingReassessment], ([data, loading]) => {
  if (!loading && data) {
    const hasItems = Boolean(data.data && data.data.length > 0)
    hasReassessmentItems.value = hasItems

    if (hasItems && introViewed.value && !localStorage.getItem('reassessment_modal_never_show')) {
      const seeLaterDate = localStorage.getItem('reassessment_modal_see_later_date')
      if (seeLaterDate) {
        openModal.value = checkSeeLaterPeriod(seeLaterDate) && !checkIfThreeDaysHavePassed(seeLaterDate)
      }
      else {
        openModal.value = true
      }
    }
    else if (hasItems && !introViewed.value) {
      hasPendingReassessmentModal.value = true
    }
  }
})

watch(introViewed, (nowViewed) => {
  if (!nowViewed) return
  if (!hasPendingReassessmentModal.value) return
  if (!hasReassessmentItems.value) return
  if (localStorage.getItem('reassessment_modal_never_show')) return

  const seeLaterDate = localStorage.getItem('reassessment_modal_see_later_date')
  if (seeLaterDate) {
    openModal.value = checkSeeLaterPeriod(seeLaterDate) && !checkIfThreeDaysHavePassed(seeLaterDate)
  }
  else {
    openModal.value = true
  }
  hasPendingReassessmentModal.value = false
}, { immediate: false })

function newSimulation() {
  router.push(userIsOnBlockedList.value ? '/projects' : '/simulation/new')
}

watch(() => impersonateStore.partnerId, () => {
  setParams({ ...paramsFilters.value, partner_id: impersonateStore.partnerId })
})

watch(partner, (newPartner) => {
  if (newPartner?.blocklist?.is_blocked) {
    router.push({ path: '/projects', query: route.query })
  }
})

let isUpdatingFromUrl = false

function buildQueryFromParams(params: UseFinancingsParams, provider?: string) {
  return {
    page: params.page.toString(),
    steps: params.steps.length > 0 ? params.steps.join(',') : undefined,
    status: params.status.length > 0 ? params.status.join(',') : undefined,
    query: params.query || undefined,
    order_by: params.order_by || undefined,
    provider: provider !== undefined ? provider : (params.provider || undefined),
    is_installed: params.is_installed !== undefined ? params.is_installed.toString() : undefined,
    formalized_lte: params.formalized_lte || undefined,
    formalized_gte: params.formalized_gte !== undefined ? params.formalized_gte : undefined,
    is_expired: params.is_expired !== undefined ? params.is_expired.toString() : undefined,
    is_canceled: params.is_canceled !== undefined ? params.is_canceled.toString() : undefined,
    combo_type: comboTypeFromParams(params) !== COMBO_TYPE_FILTER.ALL ? comboTypeFromParams(params) : undefined,
  }
}

watch(() => paramsFilters.value, (_filters) => {
  if (isUpdatingFromUrl) return

  router.push({
    query: buildQueryFromParams(paramsFilters.value),
  })
}, { deep: true })

watch(() => reassessmentParams.value, (_filters) => {
  if (isUpdatingFromUrl) return

  const activeTab = tabListModel.value.find(item => item.active)?.key
  if (activeTab !== 'reassessment') return

  router.push({
    query: buildQueryFromParams(reassessmentParams.value, 'reassessment'),
  })
}, { deep: true })

watch(() => route.query, (newQuery) => {
  isUpdatingFromUrl = true

  const parsed = parseQueryParams(newQuery)
  const matchingSortOption = sortByOptions.find(option => option.value === parsed.order_by)
  if (matchingSortOption) {
    sortBy.value = matchingSortOption
  }

  if (parsed.provider === 'reassessment') {
    setReassessmentParams(buildParamsFromQuery(newQuery, reassessmentParams.value))
  }
  else {
    setParams(buildParamsFromQuery(newQuery, paramsFilters.value))
  }

  nextTick(() => {
    isUpdatingFromUrl = false
  })
}, { immediate: true })

function handleQuickFilterActive(hasActive: boolean) {
  hasActiveQuickFilter.value = hasActive
}

function handleTabChange() {
  const activeTab = tabListModel.value.find(item => item.active)?.key
  const currentOrderBy = route.query.order_by?.toString() || 'updated_at:desc'

  isUpdatingFromUrl = true

  if (activeTab === 'reassessment') {
    clearReassessmentFilters()
    setReassessmentParams({
      ...reassessmentParams.value,
      order_by: currentOrderBy,
    })
  }
  else {
    clearFiltersBase()
    setParams({
      ...paramsFilters.value,
      order_by: currentOrderBy,
      provider: '',
    })
  }

  router.replace({
    query: {
      page: '1',
      order_by: currentOrderBy !== 'updated_at:desc' ? currentOrderBy : undefined,
      provider: activeTab === 'reassessment' ? 'reassessment' : undefined,
    },
  })

  nextTick(() => {
    isUpdatingFromUrl = false
  })
}

function markAsViewed() {
  localStorage.setItem('reassessment_modal_viewed', 'true')
}

function checkSeeLaterPeriod(seeLaterDate: string | null): boolean {
  if (!seeLaterDate)
    return false

  const storedDate = new Date(seeLaterDate)
  const currentDate = new Date()
  const storedDateOnly = new Date(storedDate.getFullYear(), storedDate.getMonth(), storedDate.getDate())
  const currentDateOnly = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate())

  return storedDateOnly.getTime() !== currentDateOnly.getTime()
}

function checkIfThreeDaysHavePassed(seeLaterDate: string | null): boolean {
  if (!seeLaterDate)
    return false

  const storedDate = new Date(seeLaterDate)
  const currentDate = new Date()
  const diffTime = currentDate.getTime() - storedDate.getTime()
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))

  return diffDays >= 3
}

function handleGoToReassessment() {
  markAsViewed()
  const currentDate = new Date().toISOString()
  localStorage.setItem('reassessment_modal_see_later_date', currentDate)
  openModal.value = false
  tabListModel.value[1].active = true
  tabListModel.value[0].active = false
  handleTabChange()
}

function handleNeverShowAgain() {
  localStorage.setItem('reassessment_modal_never_show', 'true')
  localStorage.removeItem('reassessment_modal_see_later_date')
  openModal.value = false
}

function handleSeeLater() {
  const currentDate = new Date().toISOString()
  localStorage.setItem('reassessment_modal_see_later_date', currentDate)
  openModal.value = false
}

function goToRevalidations() {
  if (route.path === '/projects' && route.query.provider === 'revalidation') {
    activateTabByKey('revalidation')
    return
  }
  router.replace({ path: '/projects', query: { provider: 'revalidation' } })
}

onMounted(() => {
  syncIntroViewed()
  window.addEventListener('storage', syncIntroViewed)
  introCheckInterval = setInterval(syncIntroViewed, 1000)
})

onBeforeUnmount(() => {
  window.removeEventListener('storage', syncIntroViewed)
  if (introCheckInterval)
    clearInterval(introCheckInterval)
})

watch(() => tabListModel.value, handleTabChange)

watch(sortBy, (sortOption) => {
  if (isUpdatingFromUrl) return

  const activeTab = tabListModel.value.find(item => item.active)?.key

  if (activeTab === 'reassessment') {
    if (sortOption.value === reassessmentParams.value.order_by) return
    setReassessmentParams({ ...reassessmentParams.value, order_by: sortOption.value, page: 1 })
    return
  }

  if (sortOption.value === paramsFilters.value.order_by) return
  setParams({ ...paramsFilters.value, order_by: sortOption.value, page: 1 })
}, { flush: 'post' })

watch(bnplFeatureFlag, (value) => {
  isEnabledToBNPL.value = value || false
}, { immediate: true })
</script>

<template>
  <div class="initial-page-container">
    <div class="page-heading">
      <h2 class="fonts-heading-h2">
        {{ t('home.title') }}
      </h2>

      <SolButton
        v-if="auth?.person.partner?.permission.financing"
        id="new-simulation"
        :disabled="userIsOnBlockedList"
        :loading="isLoadingPartner"
        @click="newSimulation"
      >
        <template #icon-left>
          <IconPlus />
        </template>
        {{ t('home.newSimulation') }}
      </SolButton>
    </div>

    <SharedMessageInstallmentsProjects v-if="isEnabledToBNPL" />

    <RevalidationBannerGroup
      :visible="hasIncentiveBanner"
      :image="incentiveData?.banner_url ?? undefined"
      :image-alt="revalidationBannerAlt"
      :title="incentiveData?.title_ptbr"
      :description="incentiveData?.description_ptbr"
      :cta-label="revalidationBannerCtaLabel"
      @cta-click="goToRevalidations"
    >
      <SolTabs
        id="financing-tabs"
        v-model:tab-list="tabListModel"
        class="financing-tabs"
        :class="{ 'financing-tabs--no-banner': !hasIncentiveBanner }"
      >
        <template #tab:all>
          <div class="financing-tabs-content">
            <FinancingsFilter
              :initial-value="paramsFilters"
              :financings="financings"
              :has-active-quick-filter="hasActiveQuickFilter"
              @apply-filters="applyFilters"
              @apply-combo-type="applyComboType"
              @clear-filters="clearFiltersBase"
            />
            <ListActions
              v-model:sort-by="sortBy"
              :sort-by-options="sortByOptions"
              :params="paramsFilters"
              @update:params="handleQuickFilterChange"
              @has-active-quick-filter="handleQuickFilterActive"
            />
            <ListProjects
              :loading="isLoadingFinancings"
              :list="financings"
              :error-financings="errorFinancings"
              :params="paramsFilters"
              :prefetch-page="prefetchPage"
              tab-type="all"
              @set-filters="setParams"
              @clear-filters="clearFiltersBase"
            />
          </div>
        </template>

        <template #tab:reassessment>
          <FinancingsFilter
            :initial-value="reassessmentParams"
            :financings="reassessmentFinancings"
            :has-active-quick-filter="hasActiveQuickFilter"
            @apply-filters="applyReassessmentFilters"
            @apply-combo-type="applyReassessmentComboType"
            @clear-filters="clearReassessmentFilters"
          />
          <ListActions
            v-model:sort-by="sortBy"
            :sort-by-options="sortByOptions"
            :params="reassessmentParams"
            @update:params="handleReassessmentQuickFilterChange"
            @has-active-quick-filter="handleQuickFilterActive"
          />
          <ListProjects
            :loading="isLoadingReassessment"
            :list="reassessmentFinancings"
            :error-financings="errorFinancings"
            :params="reassessmentParams"
            :prefetch-page="prefetchReassessmentPage"
            tab-type="reassessment"
            @set-filters="setReassessmentParams"
            @clear-filters="clearReassessmentFilters"
          />
        </template>

        <template v-if="revalidationTabFlag" #tabitem:revalidation>
          <span class="revalidation-tab-label">
            Revalidação
            <span v-if="revalidationSummary?.total != null" class="revalidation-tab-badge">
              {{ revalidationSummary.total }}
            </span>
          </span>
        </template>

        <template v-if="revalidationTabFlag" #tab:revalidation>
          <RevalidationsTab />
        </template>
      </SolTabs>
    </RevalidationBannerGroup>

    <ReassessmentModal
      :is-open="openModal"
      @close="openModal = false"
      @go-to-reassessment="handleGoToReassessment"
      @never-show-again="handleNeverShowAgain"
      @see-later="handleSeeLater"
    />

    <RevalidationWelcomeModal
      v-if="incentiveData"
      :is-open="revalidationWelcomeModalOpen"
      :image="incentiveData.banner_url ?? ''"
      :image-alt="revalidationBannerAlt"
      :title="incentiveData.title_ptbr"
      :description="incentiveData.description_ptbr"
      @close="handleRevalidationWelcomeClose"
      @never-show-again="handleRevalidationWelcomeNeverShowAgain"
      @go-to-revalidation="handleGoToRevalidationFromWelcome"
    />
  </div>
</template>

<style scoped lang="scss">
.initial-page-container {
  @apply flex flex-col py-2xs px-4xs gap-4;

  @screen md:system {
    @apply px-2xs pb-sm pt-2xs;
  }

  .page-heading {
    @apply flex items-center justify-between gap-4;

    @screen md:system {
      @apply flex-row items-center justify-between;
    }
  }

  .financing-tabs {
    @apply bg-neutral-high-pure rounded-b-md p-2xs w-full;

    &.financing-tabs--no-banner {
      @apply rounded-t-md;
    }
  }

  .revalidation-tab-label {
    @apply flex items-center gap-1;

    > .revalidation-tab-badge {
      @apply flex items-center justify-center;
      @apply min-w-5 h-5 px-[5px] py-[2px];
      @apply rounded-full;
      @apply bg-neutral-low-pure text-neutral-high-pure;
      @apply fonts-body-small-bold;
    }
  }
}
</style>
